import { MediaResult } from '@euler/app/components/media/types';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { BlurView } from 'expo-blur';
import { memo } from 'react';
import { StyleSheet, ViewProps } from 'react-native';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';
import Animated, {
  Extrapolate,
  interpolate,
  runOnJS,
  useAnimatedStyle,
  useDerivedValue,
  useSharedValue,
  withSpring,
} from 'react-native-reanimated';

const kButtonSize = 70;
const kBorderWidth = 6;
const kMaxScale = 1.3;
const kMaxPanRatio = 0.3;

export type MediaCaptureCallback = (media: MediaResult) => void;

interface Props extends ViewProps {
  minZoom: number;
  maxZoom: number;
  zoom: Animated.SharedValue<number>;
  onTakePhoto: () => void;
}

export const MobileWebCaptureButton = memo(
  ({ minZoom, maxZoom, zoom, style, onTakePhoto, ...props }: Props) => {
    const { rpx } = useResponsiveContext();

    //#region Gesture handlers

    const lastTapTime = useSharedValue(0);
    const isPressingButton = useSharedValue(false);
    const pressStartTime = useSharedValue(0);

    const tapGesture = Gesture.Tap()
      .shouldCancelWhenOutside(false)
      // prevent the tap gesture to fail to fast (default 500ms)
      .maxDuration(500)
      .onBegin(() => {
        const now = Date.now();
        const diff = now - lastTapTime.value;

        lastTapTime.value = now;

        if (diff < 1000) {
          return;
        }

        pressStartTime.value = now;

        isPressingButton.value = true;
      })
      .onEnd((_, success) => {
        if (!isPressingButton.value) {
          return;
        }

        isPressingButton.value = false;

        if (pressStartTime.value === 0 || !success) {
          __DEV__ && console.log('abort');
          return;
        }

        const now = Date.now();
        const diff = now - pressStartTime.value;
        __DEV__ &&
          console.log('start time: ', pressStartTime.value, ', diff: ', diff);
        pressStartTime.value = 0;

        runOnJS(onTakePhoto)();
      });

    // pan gesture context variables
    const panStartY = useSharedValue(0);
    const panMinY = useSharedValue(0);
    const panMaxOffset = useSharedValue(0);
    const panInitialOffset = useSharedValue(0);
    const panGesture = Gesture.Pan()
      .shouldCancelWhenOutside(false)
      .activeOffsetY([-2, 2])
      .onBegin(e => {
        panStartY.value = e.absoluteY;
        panMinY.value = panStartY.value * kMaxPanRatio;
        panMaxOffset.value = panStartY.value - panMinY.value;
        panInitialOffset.value = interpolate(
          zoom.value,
          [minZoom, maxZoom],
          [0, panMaxOffset.value],
          Extrapolate.CLAMP,
        );
      })
      .onUpdate(e => {
        zoom.value = interpolate(
          e.absoluteY - panInitialOffset.value,
          [panMinY.value, panStartY.value],
          [maxZoom, minZoom],
          Extrapolate.CLAMP,
        );
      });

    //#endregion

    const gesture = Gesture.Simultaneous(tapGesture, panGesture);

    //#region Button style

    const scale = useDerivedValue(() => {
      return withSpring(interpolate(0, [0, 1], [1, kMaxScale]), {
        damping: 10,
        stiffness: 300,
      });
    });

    const buttonStyle = useAnimatedStyle(() => {
      return {
        transform: [
          {
            scale: scale.value,
          },
        ],
      };
    });

    const innerButtonStyle = useAnimatedStyle(() => {
      return {
        backgroundColor: '#fff',
        transform: [
          {
            scale: interpolate(scale.value, [1, kMaxScale], [1, 0.4]),
          },
        ],
      };
    });

    //#endregion

    const buttonSize = rpx(kButtonSize);
    const borderWidth = rpx(kBorderWidth);

    return (
      <GestureDetector gesture={gesture}>
        <Animated.View
          css={`
            width: ${buttonSize}px;
            height: ${buttonSize}px;
            border-radius: ${buttonSize / 2}px;
            align-items: center;
            justify-content: center;
          `}
          style={[style, buttonStyle]}
          {...props}
        >
          <BlurView
            style={StyleSheet.absoluteFill}
            css={`
              width: ${buttonSize}px;
              height: ${buttonSize}px;
              border-radius: ${buttonSize / 2}px;
              overflow: hidden;
            `}
            intensity={100}
            tint="default"
          />
          <Animated.View
            css={`
              width: ${buttonSize - borderWidth * 2}px;
              height: ${buttonSize - borderWidth * 2}px;
              border-radius: ${(buttonSize - borderWidth * 2) / 2}px;
            `}
            style={innerButtonStyle}
          />
        </Animated.View>
      </GestureDetector>
    );
  },
);
